import React, { useState } from 'react';
import { Wrench, Download, FileText, AlertCircle } from 'lucide-react';
import { Button, Card, FileUploadZone, ProgressBar, Alert, Badge, PDFPreview, NoteCard } from '../shared';
import { PDFDocument } from 'pdf-lib';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import '../../styles/design-system.css';

const RepairPDF = () => {
    const [file, setFile] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [repairStatus, setRepairStatus] = useState(null);
    const [previewBlob, setPreviewBlob] = useState(null);
    const [previewFilename, setPreviewFilename] = useState('');

    const handleFileSelected = async (files) => {
        if (files && files.length > 0) {
            const selectedFile = files[0];
            setFile(selectedFile);
            setRepairStatus(null);
            toast.success('PDF loaded - ready to repair');
        }
    };

    const repairPDF = async () => {
        if (!file) return;

        try {
            setIsProcessing(true);
            setProgress(10);
            setRepairStatus({ status: 'analyzing', message: 'Analyzing PDF structure...' });

            // Load the PDF
            const arrayBuffer = await file.arrayBuffer();
            setProgress(30);

            try {
                // Attempt to load and repair the PDF
                const pdfDoc = await PDFDocument.load(arrayBuffer, {
                    ignoreEncryption: true,
                    updateMetadata: false
                });

                setProgress(50);
                setRepairStatus({ status: 'repairing', message: 'Repairing PDF structure...' });

                // Get page count to verify structure
                const pageCount = pdfDoc.getPageCount();
                setProgress(70);

                // Create a new PDF and copy all pages (this rebuilds the structure)
                const repairedPdf = await PDFDocument.create();

                for (let i = 0; i < pageCount; i++) {
                    const [copiedPage] = await repairedPdf.copyPages(pdfDoc, [i]);
                    repairedPdf.addPage(copiedPage);
                    setProgress(70 + ((i + 1) / pageCount) * 20);
                }

                setProgress(95);
                setRepairStatus({ status: 'success', message: `Successfully repaired ${pageCount} pages` });

                // Save the repaired PDF
                const pdfBytes = await repairedPdf.save();
                const blob = new Blob([pdfBytes], { type: 'application/pdf' });

                setProgress(100);

                // Set preview
                const filename = `repaired-${file.name}`;
                setPreviewBlob(blob);
                setPreviewFilename(filename);

                toast.success('PDF repaired! Opening preview...');

                setTimeout(() => {
                    setProgress(0);
                }, 1000);

            } catch (loadError) {
                // If loading fails, try alternative repair method
                setRepairStatus({ status: 'error', message: 'PDF is severely corrupted. Attempting alternative repair...' });
                setProgress(50);

                // Alternative: Try to extract whatever we can
                try {
                    const pdfDoc = await PDFDocument.load(arrayBuffer, {
                        ignoreEncryption: true,
                        throwOnInvalidObject: false
                    });

                    const pageCount = pdfDoc.getPageCount();
                    const repairedPdf = await PDFDocument.create();

                    let repairedPages = 0;
                    for (let i = 0; i < pageCount; i++) {
                        try {
                            const [copiedPage] = await repairedPdf.copyPages(pdfDoc, [i]);
                            repairedPdf.addPage(copiedPage);
                            repairedPages++;
                        } catch (pageError) {
                            console.warn(`Could not repair page ${i + 1}`);
                        }
                        setProgress(50 + ((i + 1) / pageCount) * 45);
                    }

                    if (repairedPages > 0) {
                        const pdfBytes = await repairedPdf.save();
                        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
                        setProgress(100);

                        // Set preview
                        const filename = `partially-repaired-${file.name}`;
                        setPreviewBlob(blob);
                        setPreviewFilename(filename);

                        setRepairStatus({
                            status: 'partial',
                            message: `Partially repaired: ${repairedPages}/${pageCount} pages recovered`
                        });
                        toast.warning(`Partially repaired: ${repairedPages}/${pageCount} pages`);
                    } else {
                        throw new Error('Could not repair any pages');
                    }

                } catch (altError) {
                    setRepairStatus({
                        status: 'failed',
                        message: 'PDF is too corrupted to repair automatically'
                    });
                    toast.error('PDF is too corrupted to repair');
                }
            }

        } catch (error) {
            console.error('Repair error:', error);
            setRepairStatus({ status: 'failed', message: error.message });
            toast.error(`Failed to repair PDF: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div>
            {/* Header */}
            <div className="tool-header">
                <h1 className="tool-title">
                    <Wrench size={36} style={{ display: 'inline', marginRight: 'var(--spacing-3)', color: 'var(--color-accent)' }} />
                    Repair PDF
                </h1>
                <p className="tool-description">
                    Fix corrupted or damaged PDF files. Automatically repairs PDF structure and recovers readable pages.
                </p>
            </div>

            {/* File Upload */}
            {!file ? (
                <FileUploadZone
                    onFilesSelected={handleFileSelected}
                    acceptedTypes={['application/pdf']}
                    multiple={false}
                    title="Drop corrupted PDF here"
                    description="or click to browse"
                />
            ) : (
                <Alert variant="warning" title="PDF Loaded">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                            <FileText size={16} />
                            <span>{file.name}</span>
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                                setFile(null);
                                setRepairStatus(null);
                            }}
                        >
                            Change PDF
                        </Button>
                    </div>
                </Alert>
            )}

            {file && (
                <>
                    {/* Repair Status */}
                    {repairStatus && (
                        <div style={{ marginTop: 'var(--spacing-6)' }}>
                            <Alert
                                variant={
                                    repairStatus.status === 'success' ? 'success' :
                                        repairStatus.status === 'partial' ? 'warning' :
                                            repairStatus.status === 'failed' ? 'error' : 'info'
                                }
                                title={
                                    repairStatus.status === 'analyzing' ? 'Analyzing...' :
                                        repairStatus.status === 'repairing' ? 'Repairing...' :
                                            repairStatus.status === 'success' ? 'Repair Successful' :
                                                repairStatus.status === 'partial' ? 'Partial Repair' :
                                                    'Repair Failed'
                                }
                            >
                                {repairStatus.message}
                            </Alert>
                        </div>
                    )}

                    {/* Info */}
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Card>
                            <h3 style={{
                                fontSize: 'var(--text-lg)',
                                fontWeight: 'var(--font-bold)',
                                marginBottom: 'var(--spacing-3)'
                            }}>
                                <AlertCircle size={20} style={{ display: 'inline', marginRight: 'var(--spacing-2)' }} />
                                What This Tool Does
                            </h3>
                            <ul style={{
                                fontSize: 'var(--text-sm)',
                                color: 'var(--color-text-secondary)',
                                lineHeight: 1.6,
                                paddingLeft: 'var(--spacing-5)'
                            }}>
                                <li>Analyzes PDF structure for corruption</li>
                                <li>Rebuilds damaged PDF structure</li>
                                <li>Recovers readable pages</li>
                                <li>Removes invalid objects</li>
                                <li>Creates a clean, working PDF</li>
                            </ul>
                        </Card>
                    </div>

                    {/* Progress Bar */}
                    {isProcessing && (
                        <div style={{ marginTop: 'var(--spacing-6)' }}>
                            <ProgressBar value={progress} striped showLabel />
                        </div>
                    )}

                    {/* Repair Button */}
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Button
                            variant="primary"
                            size="lg"
                            onClick={repairPDF}
                            disabled={isProcessing}
                            loading={isProcessing}
                            fullWidth
                        >
                            <Wrench size={20} />
                            Repair PDF
                        </Button>
                    </div>

                    {/* Warning */}
                    <NoteCard variant="warning">
                        <strong>Note:</strong> Repair success depends on the extent of corruption. Severely damaged PDFs may only be partially recoverable.
                    </NoteCard>
                </>
            )}

            {/* PDF Preview Modal */}
            {previewBlob && (
                <PDFPreview
                    pdfBlob={previewBlob}
                    filename={previewFilename}
                    onClose={() => setPreviewBlob(null)}
                    onDownload={() => {
                        if (previewBlob && previewFilename) {
                            saveAs(previewBlob, previewFilename);
                            toast.success('Download started!');
                            setPreviewBlob(null);
                        }
                    }}
                />
            )}
        </div>
    );
};

export default RepairPDF;
